/**
 * OrthoAI Integration — AI Analysis & PDF Export
 * Connects U-DICOM Viewer to OrthoAI backend for radiological AI analysis
 */

const ORTHOAI_API = 'https://orthoai-backend.castrimail.workers.dev'

function getToken() {
  return localStorage.getItem('orthoai_token')
}

/**
 * Send visible DICOM images to OrthoAI for AI radiological analysis
 * @param {Array<{name: string, data: string}>} images — base64 JPG images
 * @returns {Promise<string>} AI report text
 */
export async function aiAnalyze(images) {
  let token = getToken()

  if (!token) {
    token = await promptLogin()
    if (!token) throw new Error('Login richiesto per l\'analisi AI')
  }

  const res = await fetch(ORTHOAI_API + '/api/ai/analyze-image', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token,
    },
    body: JSON.stringify({
      images: images.map(img => ({
        name: img.name,
        data: img.data,
        mediaType: 'image/jpeg',
      })),
      prompt: 'Sei un radiologo esperto. Analizza queste proiezioni di uno studio DICOM e genera un referto radiologico strutturato in italiano. Includi: Tecnica, Reperti, Conclusioni.',
    }),
  })

  if (res.status === 401) {
    localStorage.removeItem('orthoai_token')
    throw new Error('Sessione scaduta. Riprova per effettuare il login.')
  }

  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Errore nell\'analisi AI')

  return data.response || data.report || data.text || JSON.stringify(data)
}

/**
 * Simple login prompt — opens a dialog for OrthoAI credentials
 */
function promptLogin() {
  return new Promise((resolve) => {
    const email = window.prompt('OrthoAI Login — Email:')
    if (!email) { resolve(null); return }
    const password = window.prompt('OrthoAI Login — Password:')
    if (!password) { resolve(null); return }

    fetch(ORTHOAI_API + '/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.token) {
          localStorage.setItem('orthoai_token', data.token)
          localStorage.setItem('orthoai_user', JSON.stringify({ email }))
          resolve(data.token)
        } else {
          alert('Login fallito: ' + (data.error || 'credenziali non valide'))
          resolve(null)
        }
      })
      .catch(() => {
        alert('Errore di connessione al server OrthoAI')
        resolve(null)
      })
  })
}

/**
 * Export PDF with DICOM images and AI report
 * @param {Array<{name: string, dataUrl: string}>} images
 * @param {string|null} report
 */
export async function exportPDF(images, report) {
  const jsPDF = await loadJsPDF()
  const doc = new jsPDF('p', 'mm', 'a4')
  const W = 210
  const margin = 15
  const cw = W - 2 * margin

  // Header
  doc.setFillColor(13, 21, 37)
  doc.rect(0, 0, W, 32, 'F')
  doc.setTextColor(240, 244, 255)
  doc.setFontSize(18)
  doc.setFont(undefined, 'bold')
  doc.text('DICOM AI', margin, 21)
  doc.setFontSize(10)
  doc.setFont(undefined, 'normal')
  doc.setTextColor(148, 163, 184)
  doc.text('Elaborazione e Analisi Radiologica', margin + 48, 21)
  const now = new Date()
  doc.text(
    now.toLocaleDateString('it-IT') + ' ' + now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
    W - margin, 21, { align: 'right' }
  )

  let y = 42

  // Images
  doc.setTextColor(60, 60, 60)
  doc.setFontSize(13)
  doc.setFont(undefined, 'bold')
  doc.text('Proiezioni DICOM', margin, y)
  y += 8

  const imgCount = Math.min(images.length, 3)
  const imgW = (cw - (imgCount - 1) * 5) / imgCount
  const imgH = imgW

  images.slice(0, 3).forEach((img, i) => {
    const x = margin + i * (imgW + 5)
    try {
      doc.addImage(img.dataUrl, 'JPEG', x, y, imgW, imgH)
      doc.setFontSize(8)
      doc.setFont(undefined, 'bold')
      doc.setTextColor(100, 100, 100)
      doc.text(img.name.toUpperCase(), x + imgW / 2, y + imgH + 5, { align: 'center' })
    } catch (e) {
      // skip if image fails
    }
  })
  y += imgH + 16

  // Report
  if (report) {
    doc.setFontSize(13)
    doc.setFont(undefined, 'bold')
    doc.setTextColor(60, 60, 60)
    doc.text('Referto AI', margin, y)
    y += 7
    doc.setFontSize(10)
    doc.setFont(undefined, 'normal')
    doc.setTextColor(40, 40, 40)
    doc.splitTextToSize(report, cw).forEach((line) => {
      if (y > 275) { doc.addPage(); y = 20 }
      doc.text(line, margin, y)
      y += 5
    })
  }

  // Footer
  const pages = doc.getNumberOfPages()
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p)
    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    doc.text('Generato da DICOM AI — Viewer & Analisi Radiologica', margin, 290)
    doc.text('Pagina ' + p + '/' + pages, W - margin, 290, { align: 'right' })
  }

  doc.save('referto-dicom-ai.pdf')
}

function loadJsPDF() {
  return new Promise((resolve, reject) => {
    if (window.jspdf) { resolve(window.jspdf.jsPDF); return }
    const s = document.createElement('script')
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.2/jspdf.umd.min.js'
    s.onload = () => resolve(window.jspdf.jsPDF)
    s.onerror = () => reject(new Error('Impossibile caricare jsPDF'))
    document.head.appendChild(s)
  })
}
